import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const ctaFinalSchema = z.object({
  eyebrow: z.string().max(80).optional(),
  title: z.string().min(1).max(300),
  highlight: z.string().max(160).optional(),
  subtitle: z.string().max(500).optional(),
  cards: z
    .array(
      z.object({
        variant: z.enum(['accel', 'ma']).default('accel'),
        tag: z.string().max(80).optional(),
        title: z.string().min(1).max(160),
        href: z.string().min(1).max(500),
      }),
    )
    .min(1)
    .max(3),
});

export type CtaFinalData = z.infer<typeof ctaFinalSchema>;

export function CtaFinalRenderer({
  eyebrow,
  title,
  highlight,
  subtitle,
  cards,
}: CtaFinalData) {
  const safeCards = Array.isArray(cards) ? cards : [];
  return (
    <section className="v5-section v5-cta-page">
      <div className="v5-cta-page-bg"></div>
      <div className="container">
        <div className="v5-cta-page-head">
          {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
          <h2 className="v5-cta-page-h2">
            {title}
            {highlight ? <> <span className="hl-lime">{highlight}</span></> : null}
          </h2>
          {subtitle ? <p className="v5-cta-page-sub">{subtitle}</p> : null}
        </div>
        <div className="v5-cta-page-grid">
          {safeCards.map((c, i) => (
            <a key={i} href={c.href} className={`v5-cta-page-card v5-cta-page-card-${c.variant}`}>
              {c.tag ? (
                <div className="v5-cta-page-tag">
                  <span className={`dot ${c.variant}`}></span>
                  {c.tag}
                </div>
              ) : null}
              <div className="v5-cta-page-title">{c.title}</div>
              <div className="v5-cta-page-arrow">→</div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export const ctaFinalBlock: BlockDefinition<CtaFinalData> = {
  meta: {
    type: 'cta-final',
    label: 'CTA final (v5)',
    description: 'CTA de cierre con título destacado y dos tarjetas de acción (accel / ma).',
    category: 'cta',
    icon: 'megaphone',
  },
  schema: ctaFinalSchema,
  defaults: {
    eyebrow: '● Empieza por aquí',
    title: 'Antes de decidir nada —',
    highlight: 'conoce el valor real de tu empresa.',
    subtitle: 'Valoración orientativa gratuita en 7 días. Sin compromiso, sin coste, 100% confidencial.',
    cards: [
      { variant: 'accel', tag: 'Sin coste · 7 días', title: 'Solicita valoración gratis', href: '/valorar-empresa#formulario' },
      { variant: 'ma', tag: 'Habla primero', title: 'Llamada confidencial 30 min', href: '/contacto#agendar-ma' },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 80 },
    { name: 'title', label: 'Título', type: 'text', required: true, max: 300 },
    { name: 'highlight', label: 'Texto destacado (lima)', type: 'text', max: 160 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 500 },
    {
      name: 'cards',
      label: 'Tarjetas CTA',
      type: 'array',
      max: 3,
      itemFields: [
        {
          name: 'variant',
          label: 'Variante color',
          type: 'select',
          options: [
            { label: 'Aceleradora (lima)', value: 'accel' },
            { label: 'M&A (azul)', value: 'ma' },
          ],
        },
        { name: 'tag', label: 'Tag arriba', type: 'text', max: 80 },
        { name: 'title', label: 'Título de la card', type: 'text', required: true, max: 160 },
        { name: 'href', label: 'URL destino', type: 'url', required: true },
      ],
    },
  ],
  Renderer: CtaFinalRenderer,
};
